// VRG-643 — react-query hooks for SEO & AEO (`/internal/gtm/seo` since VRG-672; it was
// a tab of /internal/analytics).
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { seoApi } from 'services'
import type { SeoActionStatus, SeoCompetitorKind, SeoJob, SeoReviewStatus } from 'services'

/**
 * Everything here is read from Vera's own tables (the jobs write them on a schedule), so
 * a read never spends vendor credits. Five minutes is plenty: the fastest-moving source
 * is the nightly Search Console sync. `retry: false` — a 403 is a settled answer.
 */
const FIVE_MINUTES = 5 * 60 * 1000
const KEY = ['internal', 'seo'] as const

export const useSeoStatus = () =>
  useQuery({
    queryKey: [...KEY, 'status'],
    queryFn: () => seoApi.status(),
    staleTime: FIVE_MINUTES,
    retry: false,
  })

/** VRG-729 — open SEO pull alerts. One minute: an incident opening is what this exists for. */
export const useSeoAlerts = () =>
  useQuery({
    queryKey: [...KEY, 'alerts'],
    queryFn: () => seoApi.alerts(),
    staleTime: 60 * 1000,
    retry: false,
  })

export const useSeoKeywords = (market: string, enabled = true) =>
  useQuery({
    queryKey: [...KEY, 'keywords', market],
    queryFn: () => seoApi.keywords(market),
    staleTime: FIVE_MINUTES,
    retry: false,
    enabled,
  })

export const useSeoGsc = (
  range: { since: string; until: string },
  compare: boolean,
  enabled = true,
) =>
  useQuery({
    queryKey: [...KEY, 'gsc', range.since, range.until, compare],
    queryFn: () => seoApi.gsc({ ...range, compare }),
    staleTime: FIVE_MINUTES,
    retry: false,
    enabled,
  })

export const useSeoRankings = (market: string, adjacent = false, enabled = true) =>
  useQuery({
    queryKey: [...KEY, 'rankings', market, adjacent],
    queryFn: () => seoApi.rankings(market, adjacent),
    staleTime: FIVE_MINUTES,
    retry: false,
    enabled,
  })

/** One term's newest result page; fetched only while its dialog is open. */
export const useSeoSerp = (market: string, keywordId: string | null) =>
  useQuery({
    queryKey: [...KEY, 'serp', market, keywordId],
    queryFn: () => seoApi.serp(market, keywordId as string),
    staleTime: FIVE_MINUTES,
    retry: false,
    enabled: !!keywordId,
  })

export const useSeoPages = (range: { since: string; until: string }, enabled = true) =>
  useQuery({
    queryKey: [...KEY, 'pages', range.since, range.until],
    queryFn: () => seoApi.pages(range),
    staleTime: FIVE_MINUTES,
    retry: false,
    enabled,
  })

export const useSeoGaps = (market: string, enabled = true) =>
  useQuery({
    queryKey: [...KEY, 'gaps', market],
    queryFn: () => seoApi.gaps(market),
    staleTime: FIVE_MINUTES,
    retry: false,
    enabled,
  })

export const useSeoAeo = (enabled = true) =>
  useQuery({
    queryKey: [...KEY, 'aeo'],
    queryFn: () => seoApi.aeo(),
    staleTime: FIVE_MINUTES,
    retry: false,
    enabled,
  })

/**
 * VRG-695 — page titles & link previews, read LIVE from the website by the API (which
 * caches for five minutes). `refresh()` asks the API to bypass that cache and puts the
 * fresh result straight into the query, so the table never shows a second loading state.
 */
export const useSeoPageMeta = (enabled = true) => {
  const qc = useQueryClient()
  const queryKey = [...KEY, 'page-meta']
  const query = useQuery({
    queryKey,
    queryFn: () => seoApi.pageMeta(),
    staleTime: FIVE_MINUTES,
    retry: false,
    enabled,
  })
  const refresh = useMutation({
    mutationFn: () => seoApi.pageMeta(true),
    onSuccess: (data) => qc.setQueryData(queryKey, data),
  })
  return { ...query, refresh }
}

/** Any write invalidates the whole SEO tree: the sets are small and a stale cell lies. */
const useSeoMutation = <A, R>(fn: (args: A) => Promise<R>) => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: fn,
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  })
}

export const useSetKeywordOwnIt = () =>
  useSeoMutation(({ id, ownIt }: { id: string; ownIt: boolean }) => seoApi.setOwnIt(id, ownIt))

export const useRunSeoJob = () =>
  useSeoMutation(({ job, market, force }: { job: SeoJob; market?: string; force?: boolean }) =>
    seoApi.runJob(job, { market, force }),
  )

export const usePullGap = () =>
  useSeoMutation(
    (body: {
      competitor: string
      market: string
      method?: 'comparison' | 'domain_keywords'
      overlap?: boolean
    }) => seoApi.pullGap(body),
  )

// ── review (approved | proposed | dismissed — shown as "Rejected") ───────────────────────────────────────

export const useAddCompetitor = () =>
  useSeoMutation(
    (body: { domain: string; kind: SeoCompetitorKind; note?: string | null }) =>
      seoApi.addCompetitor(body),
  )

export const useSetCompetitorStatus = () =>
  useSeoMutation(
    ({
      domain,
      status,
      kind,
    }: {
      domain: string
      status: Exclude<SeoReviewStatus, 'proposed'>
      kind?: SeoCompetitorKind
    }) => seoApi.setCompetitorStatus(domain, status, kind),
  )

export const useSetCompetitorKind = () =>
  useSeoMutation(({ domain, kind }: { domain: string; kind: SeoCompetitorKind }) =>
    seoApi.setCompetitorKind(domain, kind),
  )

export const useProposeCompetitors = () => useSeoMutation(() => seoApi.proposeCompetitors())

export const useSetKeywordStatus = () =>
  useSeoMutation(
    ({ id, status }: { id: string; status: Exclude<SeoReviewStatus, 'proposed'> }) =>
      seoApi.setKeywordStatus(id, status),
  )

export const useSetKeywordTags = () =>
  useSeoMutation(
    ({
      id,
      ...tags
    }: {
      id: string
      industries?: string[]
      solutions?: string[]
      products?: string[]
    }) => seoApi.setKeywordTags(id, tags),
  )

export const useUpdateCompetitorProfile = () =>
  useSeoMutation(
    ({ domain, patch }: { domain: string; patch: Record<string, string | number | string[] | null> }) =>
      seoApi.updateCompetitorProfile(domain, patch),
  )

export const useSetGapStatus = () =>
  useSeoMutation(
    ({ id, status }: { id: string; status: Exclude<SeoReviewStatus, 'proposed'> }) =>
      seoApi.setGapStatus(id, status),
  )

export const useTrackGap = () => useSeoMutation((id: string) => seoApi.trackGap(id))

export const useAddAeoPrompt = () =>
  useSeoMutation(({ prompt, topic }: { prompt: string; topic: string | null }) =>
    seoApi.addPrompt(prompt, topic),
  )

export const usePatchAeoPrompt = () =>
  useSeoMutation(
    ({ id, ...body }: { id: string; prompt?: string; topic?: string | null; active?: boolean }) =>
      seoApi.patchPrompt(id, body),
  )

export const useSetKeywordMarkets = () =>
  useSeoMutation(({ id, markets }: { id: string; markets: string[] }) =>
    seoApi.setKeywordMarkets(id, markets),
  )

// ── recommendations → actions → reviews ───────────────────────────────────────────

export const useSeoRecommendations = (enabled = true) =>
  useQuery({
    queryKey: [...KEY, 'recommendations'],
    queryFn: () => seoApi.recommendations(),
    staleTime: FIVE_MINUTES,
    retry: false,
    enabled,
  })

export const useRecomputeRecommendations = () => useSeoMutation(() => seoApi.recompute())

export const useDecideRecommendation = () =>
  useSeoMutation(
    ({
      id,
      decision,
      note,
      days,
    }: {
      id: string
      decision: 'approve' | 'reject' | 'snooze'
      note?: string
      days?: number
    }) => seoApi.decideRecommendation(id, decision, { note, days }),
  )

export const useSetSeoActionStatus = () =>
  useSeoMutation(({ id, status, notes }: { id: string; status: SeoActionStatus; notes?: string }) =>
    seoApi.setActionStatus(id, status, notes),
  )

export const useSeoReviews = (enabled = true) =>
  useQuery({
    queryKey: [...KEY, 'reviews'],
    queryFn: () => seoApi.reviews(),
    staleTime: FIVE_MINUTES,
    retry: false,
    enabled,
  })

export const useSeoReview = (id: string | null) =>
  useQuery({
    queryKey: [...KEY, 'review', id],
    queryFn: () => seoApi.review(id as string),
    staleTime: FIVE_MINUTES,
    retry: false,
    enabled: !!id,
  })

export const useBuildSeoReview = () =>
  useSeoMutation(({ notify }: { notify: boolean }) => seoApi.buildReview(notify))

/** VRG-729 — "Mark resolved" (platform admins). No email; the incident reopens only on a new failure. */
export const useResolveSeoAlert = () =>
  useSeoMutation(({ id }: { id: string }) => seoApi.resolveAlert(id))
